import type { AppDefinition } from '@/types/app';
import { assembleOnServer } from './buildClient';
import { publishViaBackend } from './publishClient';

/**
 * Re-assembles a stored web app from its source and publishes it again.
 *
 * The stored `source` is the record of truth, so re-publishing also picks up any fix made
 * to the runtime or the assembler since the app was built — the one thing inlining the
 * runtime costs us is that those fixes do not reach an app until it is re-published, and
 * this is how they reach it.
 */
export async function republishWebApp(definition: AppDefinition): Promise<{ url: string; html: string }> {
  if (!definition.source) throw new Error('This app has no saved source, so there is nothing to publish.');

  /*
   * Assembled by the SERVER, and re-normalized there on the way out.
   *
   * An app built before a normalizer fix has the defect baked into its stored source — the
   * first published app carried a paragraph of model commentary above its UI — and
   * re-publishing is exactly the moment to clean that up without regenerating anything.
   * Doing it server-side means the app being republished picks up the CURRENT runtime and
   * the CURRENT normalizer, whatever version of this app asked for it.
   */
  const { html } = await assembleOnServer({
    appId: definition.id,
    appName: definition.name,
    source: definition.source,
    ...(definition.theme?.colors?.primary ? { accent: definition.theme.colors.primary } : {}),
    ...(definition.description ? { description: definition.description } : {}),
  });
  const { url } = await publishViaBackend(html, definition.id);
  return { url, html };
}
